import { parsePhoneNumberWithError, CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { BookableAppointmentBase } from "../backend/src/graph/bookable_appointments/bookable-appointment-base";
import { PREVIEW_SITE_APPOINTMENT_MANAGE_TO_PATIENT, PREVIEW_SITE_APPOINTMENT_PATIENT_TO_MANAGE } from "./constants";
import { nanoid, customAlphabet } from "nanoid";
import { AccessLevel } from "@dentally/enums/access-level";

export enum E_EmergencyContactRelationship {
  Spouse = "Spouse",
  Parent = "Parent",
  Sibling = "Sibling",
  Child = "Child",
  Partner = "Partner",
  Friend = "Friend",
  Other = "Other",
}

export const getCounties = (country_code: string): Array<I_CountyWithCode> => {
  switch (country_code) {
    case "CA":
      return [
        { name: "Alberta", code: "AB" },
        { name: "British Columbia", code: "BC" },
        { name: "Manitoba", code: "MB" },
        { name: "New Brunswick", code: "NB" },
        { name: "Newfoundland and Labrador", code: "NL" },
        { name: "Nova Scotia", code: "NS" },
        { name: "Ontario", code: "ON" },
        { name: "Prince Edward Island", code: "PE" },
        { name: "Quebec", code: "QC" },
        { name: "Saskatchewan", code: "SK" },
        { name: "Northwest Territories", code: "NT" },
        { name: "Nunavut", code: "NU" },
        { name: "Yukon", code: "YT" },
      ];

    default:
      return [];
  }
};

export interface I_CountyWithCode {
  name: string;
  code: string;
}

export interface I_ApptPreviewMsg {
  id: typeof PREVIEW_SITE_APPOINTMENT_MANAGE_TO_PATIENT | typeof PREVIEW_SITE_APPOINTMENT_PATIENT_TO_MANAGE;
  appointment: BookableAppointmentBase;
  brand_colour: string | null;
}

export function isValidNumber(phoneNumber: string, countryCode: string): boolean {
  try {
    return isValidPhoneNumber(phoneNumber, countryCode as CountryCode);
  } catch (err) {
    console.error("Error validating phone number", "7a074e55", { phoneNumber, countryCode }, err);
    return false;
  }
}

export function stripWhitespace(string: string): string {
  if (!string) return "";
  return string.replace(/\s/g, "").trim();
}

export function getCountyFromCode(country_code: string, patient_county: string | undefined | null): string {
  const counties = getCounties(country_code);
  const found_county = counties.find((county) => county.code === patient_county);
  if (found_county) return found_county.name;

  return patient_county || "";
}

export function normalisePhone(
  phone_number: string | undefined,
  phone_number_country: string | undefined,
  formatNational = true,
  throwError = true,
  removeWhitespace = true
): string {
  if (!phone_number || !phone_number_country) throw { code: "15fd706f", msg: "missing phone or country" };
  try {
    const phoneNumber = parsePhoneNumberWithError(phone_number, phone_number_country as CountryCode);
    let formattedNumber = "";
    if (formatNational) {
      formattedNumber = phoneNumber.formatNational();
    } else {
      formattedNumber = phoneNumber.formatInternational();
    }

    return removeWhitespace ? stripWhitespace(formattedNumber).replace(/[^0-9+]/g, "") : formattedNumber;
  } catch (err) {
    console.error("Error normalising phone", "fb1baba4", { phone_number, phone_number_country, formatNational }, err);
    if (throwError) {
      throw { code: "fb1baba4", msg: err.message };
    } else {
      return phone_number;
    }
  }
}

export function numberToWord(num: number): string {
  const words = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

  return words[num - 1] || num.toString();
}

export function formatPostcode(postcode: string): string {
  return postcode
    .toUpperCase()
    .replace(/[^\w\d\s]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function generateNanoId(domainSafe = false, length = 12, useExtendedAlphabet = false): string {
  // Create a custom nanoid that's safe to use in URIs, etc
  if (domainSafe) {
    const defaultAlphabet = "0123456789abcdefghijklmnopqrstuvwxyz-";
    const extendedAlphabet = `${defaultAlphabet}ABCDEFGHIJKLMNOPQRSTUVWXYZ`;
    const customNanoid = customAlphabet(useExtendedAlphabet ? extendedAlphabet : defaultAlphabet, length);
    let nanoId: string;
    do {
      nanoId = customNanoid();
    } while (nanoId.endsWith("-") || nanoId.startsWith("-"));
    return nanoId;
  }
  return nanoid(length);
}

export function isPrStage(stage: string): boolean {
  return !!stage.match(/^pr\d+$/);
}

export function isE2E(stage: string | undefined): boolean {
  if (!stage) return false;

  if (stage === "e2e") return true;

  return /^e2e-\w{3,4}\d$/.test(stage);
}

export function isProduction(stage: string): boolean {
  return stage.startsWith("p-");
}

export function isSandbox(stage: string): boolean {
  // Allow three or four characters for the shortened region to allow for regions like ap-southeast-1 which would be apse1
  return stage === "development" || stage === "sandbox" || /^d-\w{3,4}\d-\w\d$/.test(stage);
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isPip(access_level: AccessLevel): boolean {
  return [AccessLevel.PUBLIC_IN_PRACTICE, AccessLevel.PATIENT_IN_PRACTICE].includes(access_level);
}

export function getUserAgent(access_level: AccessLevel): string {
  if (access_level && isPip(access_level)) return "dentrv2pip";

  return "dentrv2";
}

export function compareArrays<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;

  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  return aSorted.every((val, index) => val === bSorted[index]);
}
